import React from 'react';
import { AnswerStatus, FaqItem, STATUS_META } from '../data/faqTypes';
import { parseBold } from '../data/faqText';

// One question and its answer. The status badge is the point of the card: an
// answer that is only a recommendation has to look different from one that is
// already in the pricing model.

const STATUS_CLASSES: Record<AnswerStatus, string> = {
  decided: 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]',
  proposed: 'text-[#B38600] bg-[rgba(230,161,0,0.08)]',
  open: 'text-blueprint-blue bg-[rgba(0,39,63,0.06)]',
};

const STATUS_DOT: Record<AnswerStatus, string> = {
  decided: 'bg-galvanizing-green',
  proposed: 'bg-[#E6A100]',
  open: 'bg-blueprint-blue',
};

export const StatusBadge: React.FC<{ status: AnswerStatus }> = ({ status }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap ${STATUS_CLASSES[status]}`}
  >
    <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[status]}`} />
    {STATUS_META[status].label}
  </span>
);

// Renders **bold** lead-ins inside a paragraph or bullet.
const RichText: React.FC<{ text: string }> = ({ text }) => (
  <>
    {parseBold(text).map((segment, i) =>
      segment.bold ? (
        <strong key={i} className="font-semibold text-blueprint-blue">{segment.text}</strong>
      ) : (
        <React.Fragment key={i}>{segment.text}</React.Fragment>
      )
    )}
  </>
);

const ChevronIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

interface Props {
  item: FaqItem;
  open: boolean;
  onToggle: () => void;
}

export const FaqItemCard: React.FC<Props> = ({ item, open, onToggle }) => (
  <div
    id={item.id}
    className="bg-white rounded-xl border border-[#E2E5EA] shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden scroll-mt-6"
  >
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="w-full flex items-start gap-4 px-5 py-4 text-left hover:bg-page-bg transition-colors"
    >
      <ChevronIcon
        className={`h-4 w-4 mt-0.5 flex-shrink-0 text-heritage-grey transition-transform duration-150 ${
          open ? '' : '-rotate-90'
        }`}
      />
      <h3 className="flex-1 font-heading text-[14px] font-semibold text-blueprint-blue leading-snug">
        {item.question}
      </h3>
      <StatusBadge status={item.status} />
    </button>

    {open && (
      <div className="px-5 pb-5 pl-[52px]">
        {item.answer.map((paragraph, i) => (
          <p key={i} className="text-[13px] text-text-grey leading-relaxed mb-2.5">
            <RichText text={paragraph} />
          </p>
        ))}

        {item.bullets && item.bullets.length > 0 && (
          <ul className="mt-1 mb-2.5 space-y-1.5">
            {item.bullets.map((bullet, i) => (
              <li key={i} className="flex gap-2.5 text-[13px] text-text-grey leading-relaxed">
                <span className="text-hero-red flex-shrink-0" aria-hidden>&raquo;</span>
                <span><RichText text={bullet} /></span>
              </li>
            ))}
          </ul>
        )}

        {item.note && (
          <div className="mt-3 border-l-2 border-hero-red bg-page-bg px-4 py-3 rounded-r-md">
            <p className="text-[12px] text-text-grey leading-relaxed">
              <RichText text={item.note} />
            </p>
          </div>
        )}
      </div>
    )}
  </div>
);

export default FaqItemCard;
